import { useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { get, post, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  HELP,
  apiCallFor,
  dollars,
  filterPayouts,
  missingHubHint,
  parseCommand,
  periodLabel,
  storeTitle,
  type ParsedCommand,
  type PayoutRow,
} from '@/lib/console-chat';

type Store = {
  id: string;
  fundraiserName?: string;
  schoolName?: string;
  storeNumberDisplay?: string;
  sportType?: string;
  gender?: string;
  status?: string;
  endDate?: string | null;
  store_url?: string;
  shopifyCollectionUrl?: string;
};

type Msg = { role: 'user' | 'assistant'; text: string; meta?: string; html?: boolean };

const STORE_KEY = 'gf-console-chat-store';
const EXAMPLES = [
  'list stores',
  'show store lincoln-fb',
  'schedule a drop on template general for Nov 1 8am',
  'what did we pay out last quarter',
];

function errMsg(e: unknown): string {
  const body = (e as ApiError).body as { message?: string; error?: string } | null;
  return body?.message || body?.error || (e instanceof Error ? e.message : 'Request failed');
}

function formatStoreCard(s: Store): string {
  return [
    storeTitle(s),
    `id ${s.id}`,
    `/store/${s.id}`,
    `${s.sportType || 'sport —'}${s.gender ? ` · ${s.gender}` : ''}`,
    `status ${s.status || '—'}`,
    s.endDate ? `ends ${String(s.endDate).slice(0, 10)}` : 'open-ended',
    s.store_url || s.shopifyCollectionUrl || '',
  ].filter(Boolean).join('\n');
}

function formatStoreList(stores: Store[]): string {
  const rows = stores.slice(0, 25);
  if (!rows.length) return 'No stores returned.';
  const lines = rows.map((s) => `${s.id}  ${storeTitle(s)}  ${s.status || '—'}`);
  if (stores.length > rows.length) lines.push(`… ${stores.length - rows.length} more not shown`);
  return lines.join('\n');
}

function formatPayouts(rows: PayoutRow[], parsed: Extract<ParsedCommand, { type: 'list-payouts' }>): string {
  if (!rows.length) {
    const q = parsed.year ? `${parsed.year} Q${parsed.quarter}` : 'any period';
    return `No payout statements for ${q}${parsed.storeId ? ` on ${parsed.storeId}` : ''}.`;
  }
  let team = 0;
  let rep = 0;
  const lines = rows.map((p) => {
    team += Number(p.teamCommission) || 0;
    rep += Number(p.salesRepCommission) || 0;
    return `${p.storeName || p.storeId || p.id}  ${periodLabel(p)}  team ${dollars(p.teamCommission)}  rep ${dollars(p.salesRepCommission)}`;
  });
  lines.push(`Total team ${dollars(team)} · rep ${dollars(rep)}`);
  return lines.join('\n');
}

async function resolveStore(token: string): Promise<{ store?: Store; matches?: Store[]; status?: number }> {
  try {
    const store = await get<Store>(`/api/active-stores/${encodeURIComponent(token)}`);
    return { store: { ...store, id: store.id || token } };
  } catch (e) {
    const status = e instanceof ApiError ? e.status : 0;
    if (status && status !== 404) return { status };
  }
  const list = await get<{ stores?: Store[] }>('/api/active-stores?limit=200');
  const needle = token.toLowerCase();
  const matches = (list.stores || []).filter((s) => (
    String(s.id || '').toLowerCase() === needle
    || String(s.schoolName || '').toLowerCase().includes(needle)
    || String(s.fundraiserName || '').toLowerCase().includes(needle)
    || String(s.storeNumberDisplay || '').toLowerCase() === needle
  ));
  if (matches.length === 1) return { store: matches[0] };
  if (matches.length > 1) return { matches };
  return { status: 404 };
}

export default function Chat() {
  const [storeId, setStoreId] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([
    { role: 'assistant', text: `Four commands on existing APIs:\n\n${HELP}` },
  ]);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try { setStoreId(sessionStorage.getItem(STORE_KEY) || ''); } catch { /* ignore */ }
  }, []);

  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [msgs]);

  function pin(id: string) {
    setStoreId(id);
    try {
      if (id) sessionStorage.setItem(STORE_KEY, id);
      else sessionStorage.removeItem(STORE_KEY);
    } catch { /* ignore */ }
  }

  function push(msg: Msg) {
    setMsgs((cur) => [...cur, msg]);
  }

  async function run(text: string) {
    const parsed = parseCommand(text, { storeId }, new Date());
    // 'unavailable' = we understood the sentence but the service behind it is
    // not deployed. Say so; do not dispatch a request that would 404.
    if (parsed.type === 'help' || parsed.type === 'unknown' || parsed.type === 'unavailable') {
      push({ role: 'assistant', text: parsed.message });
      return;
    }

    if (parsed.type === 'open-store') {
      try {
        const resolved = await resolveStore(parsed.storeId);
        if (resolved.store) {
          pin(resolved.store.id);
          push({ role: 'assistant', text: formatStoreCard(resolved.store), meta: `GET /api/active-stores/${resolved.store.id}` });
          return;
        }
        if (resolved.matches) {
          push({ role: 'assistant', text: `Several stores match “${parsed.storeId}”:\n${formatStoreList(resolved.matches)}`, meta: 'GET /api/active-stores' });
          return;
        }
        push({ role: 'assistant', text: `No store “${parsed.storeId}”. Try list stores.`, meta: `GET /api/active-stores/${parsed.storeId}` });
      } catch (e) {
        push({ role: 'assistant', text: errMsg(e) });
      }
      return;
    }

    const call = apiCallFor(parsed);
    if (!call) {
      push({ role: 'assistant', text: HELP });
      return;
    }
    try {
      const body = call.method === 'POST'
        ? await post<Record<string, unknown>>(call.path, call.body)
        : await get<Record<string, unknown>>(call.path);
      const meta = `${call.method} ${call.path}`;
      if (parsed.type === 'list-stores') {
        push({ role: 'assistant', text: formatStoreList((body.stores as Store[]) || []), meta });
        return;
      }
      if (parsed.type === 'list-payouts') {
        const rows = filterPayouts((body.payouts as PayoutRow[]) || [], parsed);
        push({ role: 'assistant', text: formatPayouts(rows, parsed), meta });
      }
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      const hint = missingHubHint(status, parsed);
      push({
        role: 'assistant',
        text: hint || errMsg(e),
        meta: `${call.method} ${call.path}${status ? ` → ${status}` : ''}`,
      });
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = q.trim();
    if (!text || busy) return;
    push({ role: 'user', text });
    setQ('');
    setBusy(true);
    try { await run(text); } finally { setBusy(false); }
  }

  return (
    <div className="flex h-full min-h-[calc(100vh-52px)] flex-col px-7 py-6">
      <PageHeader
        title="Store chat"
        subtitle="Structured commands on existing Console APIs. Voice later. Same four commands for every store."
      />

      <div className="mb-3 flex items-center gap-2 text-[12px] text-muted">
        {storeId ? (
          <>
            Pinned store
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11.5px] font-semibold text-primary">
              {storeId}
              <button type="button" className="text-subtle" onClick={() => pin('')}>clear</button>
            </span>
            — same commands for the next store
          </>
        ) : (
          <span>No store pinned — <code className="text-[11px]">use store &lt;id&gt;</code></span>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto rounded-[14px] border border-black/[0.05] bg-surface p-3 shadow-card">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[92%] ${m.role === 'user' ? '' : 'w-full'}`}>
              <div className={`whitespace-pre-wrap rounded-[12px] px-3 py-2 text-[13px] leading-relaxed ${
                m.role === 'user' ? 'bg-primary text-white shadow-glow' : 'bg-bg text-text'
              }`}>
                {m.role === 'assistant' && i === 0 && (
                  <MessageSquare className="mb-1.5 h-3.5 w-3.5 text-subtle" aria-hidden />
                )}
                {m.text}
              </div>
              {m.meta && <p className="mt-1 font-mono text-[10.5px] text-subtle">{m.meta}</p>}
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            className="rounded-full border border-black/[0.08] bg-surface px-2.5 py-1 text-[11.5px] text-text hover:bg-[#f7f7f9]"
            onClick={() => { setQ(ex); }}
          >
            {ex}
          </button>
        ))}
      </div>

      <form className="mt-3 flex gap-2" onSubmit={onSubmit}>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="show store lincoln-fb"
          disabled={busy}
        />
        <Button type="submit" disabled={busy || !q.trim()}>{busy ? 'Running…' : 'Run'}</Button>
      </form>
    </div>
  );
}
